/* pk-currency — display-only local-currency estimates.
   The store transacts in USD only (no Shopify Markets): every real price,
   the cart and checkout stay USD. This script rewrites the *presentation*
   of tagged prices ([data-pk-price-usd], emitted by the pk-money snippet)
   into the visitor's local currency as an estimate — "~$298 AUD" with the
   USD amount kept alongside. Rates come from a free keyless API, cached for
   24h. If anything fails (no rates, old browser, storage blocked), prices
   simply stay USD. */
(() => {
  'use strict';

  const BASE = 'USD';
  const STORE = 'pk-ccy'; // visitor's chosen currency
  const RATES = 'pk-ccy-rates'; // { t: epoch-ms, r: { AUD: 1.52, ... } }
  const MAX_AGE = 864e5; // 24h
  const API = 'https://open.er-api.com/v6/latest/USD';
  const CCYS = ['USD', 'AUD', 'CAD', 'EUR', 'GBP', 'JPY', 'NZD', 'SGD', 'HKD', 'KRW', 'CNY', 'TWD', 'CHF', 'SEK', 'NOK', 'DKK', 'INR', 'AED', 'MXN', 'BRL', 'ZAR', 'THB', 'MYR', 'PHP', 'IDR', 'PLN', 'CZK'];

  // visitor's likely currency from their IANA timezone — no network, no consent
  // needed, and wrong at worst means they see USD or flip the footer selector.
  const TZ = [
    [/^Australia\//, 'AUD'],
    [/^Pacific\/(Auckland|Chatham)/, 'NZD'],
    [/^Europe\/(London|Belfast|Guernsey|Jersey|Isle_of_Man)/, 'GBP'],
    [/^Europe\/(Dublin|Paris|Berlin|Madrid|Rome|Amsterdam|Brussels|Vienna|Lisbon|Athens|Helsinki|Luxembourg|Ljubljana|Bratislava|Riga|Tallinn|Vilnius|Malta|Nicosia|Zagreb)/, 'EUR'],
    [/^Europe\/Zurich/, 'CHF'],
    [/^Europe\/Stockholm/, 'SEK'],
    [/^Europe\/Oslo/, 'NOK'],
    [/^Europe\/Copenhagen/, 'DKK'],
    [/^Europe\/Warsaw/, 'PLN'],
    [/^Europe\/Prague/, 'CZK'],
    [/^Asia\/Tokyo/, 'JPY'],
    [/^Asia\/Seoul/, 'KRW'],
    [/^Asia\/(Shanghai|Chongqing|Urumqi)/, 'CNY'],
    [/^Asia\/Taipei/, 'TWD'],
    [/^Asia\/Singapore/, 'SGD'],
    [/^Asia\/Hong_Kong/, 'HKD'],
    [/^Asia\/(Kolkata|Calcutta)/, 'INR'],
    [/^Asia\/(Dubai|Abu_Dhabi)/, 'AED'],
    [/^Asia\/Bangkok/, 'THB'],
    [/^Asia\/(Kuala_Lumpur|Kuching)/, 'MYR'],
    [/^Asia\/Manila/, 'PHP'],
    [/^Asia\/(Jakarta|Makassar|Jayapura|Pontianak)/, 'IDR'],
    [/^America\/(Toronto|Montreal|Vancouver|Edmonton|Winnipeg|Halifax|St_Johns|Regina|Moncton|Whitehorse|Yellowknife|Iqaluit)/, 'CAD'],
    [/^America\/(Mexico_City|Cancun|Monterrey|Tijuana|Merida|Chihuahua)/, 'MXN'],
    [/^America\/(Sao_Paulo|Bahia|Fortaleza|Manaus|Recife|Belem)/, 'BRL'],
    [/^Africa\/Johannesburg/, 'ZAR'],
  ];

  const store = {
    get(k) { try { return localStorage.getItem(k); } catch (_) { return null; } },
    set(k, v) { try { localStorage.setItem(k, v); } catch (_) { /* private mode */ } },
  };

  function guess() {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      for (const [re, c] of TZ) if (re.test(tz)) return c;
    } catch (_) { /* fall through */ }
    return BASE;
  }

  let rates = null;
  let active = BASE;

  // narrowSymbol drops locale prefixes (A$ → $, CA$ → $): the ISO code after
  // the amount already says which dollar, so "~$298 AUD" not "~A$298 AUD".
  function fmtEst(cents) {
    const amt = (cents / 100) * rates[active];
    try {
      return new Intl.NumberFormat('en', { style: 'currency', currency: active, currencyDisplay: 'narrowSymbol', maximumFractionDigits: 0 }).format(amt);
    } catch (_) {
      return new Intl.NumberFormat('en', { style: 'currency', currency: active, maximumFractionDigits: 0 }).format(amt);
    }
  }

  // inner content for a tagged price span, per display mode:
  //   headline — est on its own line, "$195 USD · billed in USD" small below (PDP)
  //   paren    — est, then "($390.00 USD)" alongside (cart total)
  //   compact  — est only, USD in the tooltip (cards, size rows, buy bar)
  //   usdfirst — USD leads, local estimate in parens after (listing price lines)
  function inner(cents, mode, usd) {
    if (active === BASE || !rates || !rates[active]) {
      return mode === 'usdfirst' ? usd + '\u00a0<span class="pk-ccy">USD</span>' : usd;
    }
    const est = '~' + fmtEst(cents) + ' <span class="pk-ccy">' + active + '</span>';
    if (mode === 'headline') return '<span class="pk-price__est">' + est + '</span><span class="pk-price__usd">' + usd + ' USD · billed in USD</span>';
    if (mode === 'paren') return '<span class="pk-price__est">' + est + '</span> <span class="pk-price__usd">(' + usd + ' USD)</span>';
    if (mode === 'usdfirst') return usd + '\u00a0<span class="pk-ccy">USD</span> <span class="pk-price__est">(' + est + ')</span>';
    return '<span class="pk-price__est" title="' + usd + ' USD">' + est + '</span>';
  }

  // full tagged span — what pk-money renders server-side; used by pk-product.js
  // when it swaps variant prices, so re-applies keep working after a switch.
  function tag(cents, mode, usd) {
    return '<span class="pk-price pk-price--' + mode + '" data-pk-price-usd="' + cents + '" data-pk-price-mode="' + mode + '" data-pk-price-usd-text="' + usd + '">' + inner(cents, mode, usd) + '</span>';
  }

  function apply(root) {
    (root || document).querySelectorAll('[data-pk-price-usd]').forEach((el) => {
      el.innerHTML = inner(+el.dataset.pkPriceUsd, el.dataset.pkPriceMode || 'compact', el.dataset.pkPriceUsdText || '');
    });
  }

  function ensureRates() {
    if (rates) return Promise.resolve(true);
    try {
      const cached = JSON.parse(store.get(RATES) || 'null');
      if (cached && cached.r && Date.now() - cached.t < MAX_AGE) { rates = cached.r; return Promise.resolve(true); }
    } catch (_) { /* bad cache */ }
    return fetch(API)
      .then((r) => r.json())
      .then((d) => {
        if (d && d.result === 'success' && d.rates) {
          rates = d.rates;
          store.set(RATES, JSON.stringify({ t: Date.now(), r: rates }));
          return true;
        }
        return false;
      })
      .catch(() => false);
  }

  // persist only an explicit selector choice — the timezone auto-guess must
  // re-run each visit, or a traveller stays pinned to wherever they first browsed
  function set(c, persist = true) {
    if (CCYS.indexOf(c) === -1) c = BASE;
    active = c;
    if (persist) store.set(STORE, c);
    const sel = document.querySelector('[data-pk-ccy-select]');
    if (sel && sel.value !== c) sel.value = c;
    if (c === BASE) { apply(); return; }
    ensureRates().then((ok) => {
      if (!ok) active = BASE; // rates unavailable → honest USD
      apply();
    });
  }

  window.pkCurrency = { tag, inner, apply, set, get active() { return active; } };

  // init: stored (explicit) choice wins, else timezone guess; USD needs no work
  const sel = document.querySelector('[data-pk-ccy-select]');
  if (sel) sel.addEventListener('change', () => set(sel.value));
  const desired = store.get(STORE) || guess();
  if (desired !== BASE) set(desired, false);
  else if (sel) sel.value = BASE;

  // AJAX re-renders (cart panel, etc.): re-estimate any newly-added tagged prices
  let raf = 0;
  new MutationObserver((muts) => {
    if (active === BASE) return;
    for (const m of muts) {
      for (const n of m.addedNodes) {
        if (n.nodeType === 1 && (n.matches('[data-pk-price-usd]') || n.querySelector('[data-pk-price-usd]'))) {
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => apply());
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
